import { Contract } from '@ethersproject/contracts'
import { Web3Provider as EthersWeb3Provider } from '@ethersproject/providers'
import { Trans } from '@lingui/macro'
//import { TraceEvent } from '@uniswap/analytics'
//import { BrowserEvent, InterfaceElementName, SwapEventName } from '@uniswap/analytics-events'
import { Currency, CurrencyAmount, Percent } from '@uniswap/sdk-core'
import { Pair } from '@uniswap/v2-sdk'
import { useWeb3React } from '@web3-react/core'
import { AutoColumn } from 'components/Column'
import { LoadingOpacityContainer, loadingOpacityMixin } from 'components/Loader/styled'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import Popover from 'components/Popover'
import { isSupportedChain } from 'constants/chains'
import { getSwapCurrencyId } from 'constants/tokens'
//import { ThemedText } from '../../theme'
import useCopyClipboard from 'hooks/useCopyClipboard'
import { useFavorites } from 'hooks/useFavorites'
import useCurrencyLogoURIs from 'lib/hooks/useCurrencyLogoURIs'
import { useIsMobile } from 'nft/hooks'
import { darken } from 'polished'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Copy as CopyIcon, ExternalLink as ExternalLinkIcon, Lock, MoreVertical, PlusCircle, Shield, Star } from 'react-feather'
import styled, { useTheme } from 'styled-components/macro'
import { ThemedText } from 'theme'
import { flexColumnNoWrap, flexRowNoWrap } from 'theme/styles'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

import { ReactComponent as DropDown } from '../../assets/images/dropdown.svg'
import { useCurrencyBalance } from '../../state/connection/hooks'
import { ButtonGray } from '../Button'
import DoubleCurrencyLogo from '../DoubleLogo'
import { Input as NumericalInput } from '../NumericalInput'
import { RowBetween, RowFixed } from '../Row'
import CurrencySearchModal from '../SearchModal/CurrencySearchModal'
import { FiatValue } from './FiatValue'

const ContractAddress = styled.div`
  display: flex;
  color: ${({ theme }) => theme.textSecondary};
  margin-left: 8px;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0px;
  position: relative;
`

const ERC20_METADATA_ABI = ['function symbol() view returns (string)', 'function decimals() view returns (uint8)']
const MAX_SYMBOL_LENGTH = 11

function sanitizeSymbol(value?: string | null): string | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  return trimmed.length ? trimmed : undefined
}

const ActionDotButton = styled.button`
  width: 18px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }
`

const TokenActionsMenu = styled.div`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 168px;
  pointer-events: auto;
  box-shadow: ${({ theme }) => theme.shadow1};
  overflow: hidden;
`

const TokenActionsMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
  text-align: left;
  line-height: 1.2;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundInteractive};
  }

  svg {
    flex-shrink: 0;
  }
`

const TokenActionsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  opacity: 0.28;
  margin: 0;
`

const FavoriteIcon = styled(Star)<{ $active: boolean }>`
  stroke: ${({ theme, $active }) => ($active ? theme.accentWarning : theme.textSecondary)};
  fill: ${({ theme, $active }) => ($active ? theme.accentWarning : 'transparent')};
  transition: fill 0.2s ease, stroke 0.2s ease;
`

const InputPanel = styled.div<{ hideInput?: boolean }>`
  ${flexColumnNoWrap};
  position: relative;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  z-index: 1;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  transition: height 1s ease;
  will-change: height;
`

const FixedContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`

const Container = styled.div<{ hideInput: boolean }>`
  min-height: 44px;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
`

const CurrencySelect = styled(ButtonGray)<{
  visible: boolean
  selected: boolean
  hideInput?: boolean
  disabled?: boolean
}>`
  align-items: center;
  background-color: ${({ selected, theme }) => (selected ? theme.backgroundInteractive : theme.accentAction)};
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
  box-shadow: ${({ selected }) => (selected ? 'none' : '0px 6px 10px rgba(0, 0, 0, 0.075)')};
  color: ${({ selected, theme }) => (selected ? theme.textPrimary : theme.white)};
  cursor: pointer;
  height: unset;
  border-radius: 6px;
  outline: none;
  user-select: none;
  border: none;
  font-size: 24px;
  font-weight: 400;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  padding: ${({ selected }) => (selected ? '6px 8px 6px 8px' : '6px 6px 6px 6px')};
  gap: 8px;
  justify-content: space-between;
  margin-left: ${({ hideInput }) => (hideInput ? '0' : '12px')};
  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.xs}px`}) {
    padding: ${({ selected }) => (selected ? '4px' : '6px 6px 6px 6px')};
    margin-left: ${({ hideInput }) => (hideInput ? '0' : '8px')};
  }
  &:hover,
  &:active {
    background-color: ${({ theme, selected }) => (selected ? theme.backgroundInteractive : theme.accentAction)};
  }

  &:before {
    background-size: 100%;
    border-radius: inherit;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    content: '';
  }

  &:hover:before {
    background-color: ${({ theme }) => theme.stateOverlayHover};
  }

  &:active:before {
    background-color: ${({ theme }) => theme.stateOverlayPressed};
  }

  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`

const InputRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  justify-content: space-between;
`

const LabelRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.75rem;
  line-height: 1rem;

  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.textSecondary)};
  }
`

const FiatRow = styled(LabelRow)`
  justify-content: flex-end;
  min-height: 20px;
  padding: 8px 0px 0px 0px;
`

const Aligner = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StyledDropDown = styled(DropDown)<{ selected: boolean }>`
  margin: 0 0.25rem 0 0.35rem;
  height: 35%;
  margin-left: 5px;

  path {
    stroke: ${({ selected, theme }) => (selected ? theme.textPrimary : theme.white)};
    stroke-width: 2px;
  }
`

const StyledTokenName = styled.span<{ active?: boolean }>`
  ${({ active }) => (active ? ' margin: 0 0.25rem 0 0.25rem;' : ' margin: 0 0.25rem 0 0.25rem;')}
  ${({ active }) => (active ? ' font-size: 18px;' : ' font-size: 16px;')}
  ${({ active }) => (active ? ' font-weight: 700;' : ' font-weight: 600;')}
`

// const StyledBalanceMax = styled.button<{ disabled?: boolean }>`
// background-color: transparent;
// border: none;
// color: ${({ theme }) => theme.accentAction};
// cursor: pointer;
// font-size: 13px;
// font-weight: 700;
// opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
// //padding: 0;
// padding-left: 5px;
// padding-right: 0;
// pointer-events: ${({ disabled }) => (!disabled ? 'initial' : 'none')};
//
// :hover {
//     opacity: ${({ disabled }) => (!disabled ? 0.8 : 0.4)};
// }
//
// :focus {
//     outline: none;
// }
// `

const StyledBalanceMax = styled.button`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.accentAction};
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding-left: 5px;
  padding-right: 0;
  transition: opacity 0.2s;
  pointer-events: ${({ disabled }) => (!disabled ? 'initial' : 'none')};
  touch-action: manipulation;
  :hover {
    opacity: ${({ disabled }) => (!disabled ? 0.8 : 0.4)};
  }

  :focus {
    outline: none;
  }
`

const HorizontalDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: -5px;
  background-color: ${({ theme }) => theme.backgroundInteractive};
  border-radius: 3px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  padding: 1px 3px;
  z-index: 9999;
  display: flex;
  gap: 6px;
`

const DropdownItem = styled.div`
  padding: 1px 3px;
  font-size: 12px;
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.accentAction};
    color: ${({ theme }) => theme.white};
  }
`

const StyledNumericalInput = styled(NumericalInput)<{ $loading: boolean }>`
  ${loadingOpacityMixin};
  text-align: left;
  font-size: 30px;
  font-weight: 700;
  line-height: 37px;
  //color: ${({ theme }) => theme.textSecondary};
  font-variant: small-caps;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    font-size: 26px;
  `};
`

interface SwapCurrencyInputPanelProps {
  value: string
  onUserInput: (value: string) => void
  onMax?: () => void
  showMaxButton: boolean
  label?: ReactNode
  onCurrencySelect?: (currency: Currency) => void
  currency?: Currency | null
  hideBalance?: boolean
  pair?: Pair | null
  hideInput?: boolean
  otherCurrency?: Currency | null
  fiatValue?: { data?: number; isLoading: boolean }
  priceImpact?: Percent
  id: string
  showCommonBases?: boolean
  showCurrencyAmount?: boolean
  disableNonToken?: boolean
  renderBalance?: (amount: CurrencyAmount<Currency>) => ReactNode
  locked?: boolean
  loading?: boolean
  disabled?: boolean
  disabledInput?: boolean
}

export default function SwapCurrencyInputPanel({
  value,
  onUserInput,
  onMax,
  showMaxButton,
  onCurrencySelect,
  currency,
  otherCurrency,
  id,
  showCommonBases,
  showCurrencyAmount,
  disableNonToken,
  renderBalance,
  fiatValue,
  priceImpact,
  hideBalance = false,
  pair = null, // used for double token logo
  hideInput = false,
  locked = false,
  loading = false,
  disabled = false,
  disabledInput = false,
  ...rest
}: SwapCurrencyInputPanelProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const { account, chainId } = useWeb3React()
  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const theme = useTheme()
  const isMobile = useIsMobile()

  const handleDismissSearch = useCallback(() => {
    setModalOpen(false)
  }, [setModalOpen])

  const chainAllowed = isSupportedChain(chainId)

  const { connector } = useWeb3React()

  const logoURL = useCurrencyLogoURIs(currency)[0]
  const [actionsOpen, setActionsOpen] = useState(false)
  const [copied, setCopied] = useCopyClipboard(1500)
  const { favorites, toggle: toggleFavorite } = useFavorites()
  const currencyIdValue = useMemo(() => (currency ? getSwapCurrencyId(currency) : undefined), [currency])
  const explorerUrl = useMemo(
    () =>
      currency?.isToken && chainId ? getExplorerLink(chainId, currency.address, ExplorerDataType.TOKEN) : undefined,
    [currency, chainId]
  )
  const midgardUrl = useMemo(
    () => (currency?.isToken ? `https://midgard.wtf/contract/${currency.address}` : undefined),
    [currency]
  )
  const isFavorite = currencyIdValue ? favorites.includes(currencyIdValue.toLowerCase()) : false

  const [dropdownVisible, setDropdownVisible] = useState(false)
  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev)
  }

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleMenuClose = useCallback(
    (delay = 1200) => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
      closeTimerRef.current = setTimeout(() => setActionsOpen(false), delay)
    },
    [setActionsOpen]
  )

  const addToWalletPendingRef = useRef(false)

  useEffect(() => {
    setActionsOpen(false)
  }, [currencyIdValue, currency?.isNative])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!actionsOpen && closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [actionsOpen])

  const handleMaxClick = () => {
    if (isMobile) {
      toggleDropdown()
    } else if (typeof onMax === 'function') {
      onMax()
      setDropdownVisible(false)
    }
  }

  function formatDecimalAmount(amount: number, decimals: number): string {
    return amount.toFixed(decimals)
  }

  const handlePercentageClick = (percentage: number) => {
    if (!selectedCurrencyBalance) return
    const isNative = currency?.isNative
    const balance = parseFloat(selectedCurrencyBalance.toExact())

    const decimals = currency?.decimals || 18

    let calculatedAmount = isNative ? balance * 0.995 : balance
    calculatedAmount = calculatedAmount * (percentage / 100)

    const formattedAmount = formatDecimalAmount(calculatedAmount, decimals)

    onUserInput(formattedAmount)
    setDropdownVisible(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isMobile) return
      if (!event.target || !(event.target as HTMLElement).closest('.dropdown-container')) {
        setDropdownVisible(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isMobile])

  const addToken = useCallback(async () => {
    if (!currency?.isToken || addToWalletPendingRef.current) return

    addToWalletPendingRef.current = true

    const rawProvider = (connector as any)?.provider ?? (connector as any)?.getProvider?.()
    let web3Provider: EthersWeb3Provider | undefined

    if (rawProvider?.getSigner && rawProvider?.getNetwork) {
      web3Provider = rawProvider as EthersWeb3Provider
    } else if (rawProvider?.provider?.request) {
      web3Provider = new EthersWeb3Provider(rawProvider.provider)
    } else if (rawProvider) {
      web3Provider = new EthersWeb3Provider(rawProvider)
    }

    let symbolForRequest = sanitizeSymbol(currency.symbol)
    let decimalsForRequest = currency.decimals ?? 18

    if (web3Provider) {
      try {
        const contract = new Contract(currency.address, ERC20_METADATA_ABI, web3Provider)
        const [onChainSymbol, onChainDecimals] = await Promise.all([
          contract.symbol().catch(() => undefined),
          contract.decimals().catch(() => undefined),
        ])

        const normalizedOnChainSymbol = sanitizeSymbol(onChainSymbol)
        if (normalizedOnChainSymbol) {
          if (normalizedOnChainSymbol.length <= MAX_SYMBOL_LENGTH) {
            symbolForRequest = normalizedOnChainSymbol
          } else if (!symbolForRequest || symbolForRequest.length > MAX_SYMBOL_LENGTH) {
            symbolForRequest = undefined
          }
        }

        if (typeof onChainDecimals === 'number' && !Number.isNaN(onChainDecimals)) {
          decimalsForRequest = onChainDecimals
        }
      } catch {
        // ignore metadata fetch failures
      }
    }

    const assetOptions: any = {
      address: currency.address,
      decimals: decimalsForRequest,
      image: logoURL ?? undefined,
    }

    if (symbolForRequest && symbolForRequest.length <= MAX_SYMBOL_LENGTH) {
      assetOptions.symbol = symbolForRequest
    }

    const markIdle = () => {
      setTimeout(() => {
        addToWalletPendingRef.current = false
      }, 500)
    }

    const handleError = (error: any) => {
      const code = error?.code
      const message: string = (error?.message ?? '').toString().toLowerCase()
      if (code === 4100 || message.includes('spam filter')) {
        // treat as soft failure; wallet likely blocked duplicate request
        markIdle()
        return
      }
      markIdle()
      throw error
    }

    try {
      if (connector?.watchAsset) {
        await connector.watchAsset(assetOptions)
        markIdle()
        return
      }

      const providerRequest =
        typeof rawProvider?.request === 'function'
          ? (args: any) => rawProvider.request(args)
          : typeof rawProvider?.provider?.request === 'function'
          ? (args: any) => rawProvider.provider.request(args)
          : typeof window !== 'undefined' && typeof (window as any).ethereum?.request === 'function'
          ? (args: any) => (window as any).ethereum.request(args)
          : undefined

      if (providerRequest) {
        await providerRequest({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: assetOptions,
          },
        })
      }

      markIdle()
    } catch (error) {
      handleError(error)
    }
  }, [connector, currency, logoURL])

  const handleCopyAddress = useCallback(async () => {
    if (!currency?.isToken) return
    const address = currency.address

    let copiedWithNavigator = false
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(address)
        copiedWithNavigator = true
      }
    } catch {
      copiedWithNavigator = false
    }

    if (!copiedWithNavigator) {
      setCopied(address)
    } else {
      setCopied(address)
    }

    scheduleMenuClose()
  }, [currency, scheduleMenuClose, setCopied])

  const handleAddToWallet = useCallback(async () => {
    try {
      await addToken()
    } finally {
      scheduleMenuClose()
    }
  }, [addToken, scheduleMenuClose])

  const handleViewOnExplorer = useCallback(() => {
    if (!explorerUrl) return
    window.open(explorerUrl, '_blank', 'noopener,noreferrer')
    scheduleMenuClose(400)
  }, [explorerUrl, scheduleMenuClose])

  const handleViewOnMidgard = useCallback(() => {
    if (!midgardUrl) return
    window.open(midgardUrl, '_blank', 'noopener,noreferrer')
    scheduleMenuClose(400)
  }, [midgardUrl, scheduleMenuClose])

  const handleToggleFavorite = useCallback(() => {
    if (!currencyIdValue) return
    toggleFavorite(currencyIdValue)
    scheduleMenuClose()
  }, [currencyIdValue, toggleFavorite, scheduleMenuClose])

  return (
    <InputPanel id={id} hideInput={hideInput} {...rest}>
      {locked && (
        <FixedContainer>
          <AutoColumn gap="sm" justify="center">
            <Lock />
            <ThemedText.DeprecatedLabel fontSize="12px" textAlign="center" padding="0 12px">
              <Trans>The market price is outside your specified price range. Single-asset deposit only.</Trans>
            </ThemedText.DeprecatedLabel>
          </AutoColumn>
        </FixedContainer>
      )}
      <Container hideInput={hideInput}>
        <InputRow style={hideInput ? { padding: '0', borderRadius: '8px' } : {}}>
          {!hideInput && !disabledInput && (
            <StyledNumericalInput
              className="token-amount-input"
              value={value}
              onUserInput={onUserInput}
              disabled={!chainAllowed || disabled}
              $loading={loading}
              maxDecimals={currency?.decimals}
            />
          )}

          {disabledInput && (
            <StyledNumericalInput
              className="token-amount-input"
              value={value}
              onUserInput={onUserInput}
              disabled={true}
              $loading={loading}
              maxDecimals={currency?.decimals}
            />
          )}

          <CurrencySelect
            disabled={!chainAllowed || disabled}
            visible={currency !== undefined}
            selected={!!currency}
            hideInput={hideInput}
            className="open-currency-select-button"
            onClick={() => {
              if (onCurrencySelect) {
                setModalOpen(true)
              }
            }}
          >
            <Aligner>
              <RowFixed>
                {pair ? (
                  <span style={{ marginRight: '0.5rem' }}>
                    <DoubleCurrencyLogo currency0={pair.token0} currency1={pair.token1} size={24} margin={true} />
                  </span>
                ) : currency ? (
                  <CurrencyLogo style={{ marginRight: '2px' }} currency={currency} size="24px" />
                ) : null}
                {pair ? (
                  <StyledTokenName className="pair-name-container">
                    {pair?.token0.symbol}:{pair?.token1.symbol}
                  </StyledTokenName>
                ) : (
                  <StyledTokenName className="token-symbol-container" active={Boolean(currency && currency.symbol)}>
                    {(currency && currency.symbol && currency.symbol.length > 20
                      ? currency.symbol.slice(0, 4) +
                        '...' +
                        currency.symbol.slice(currency.symbol.length - 5, currency.symbol.length)
                      : currency?.symbol) || <Trans>Select token</Trans>}
                  </StyledTokenName>
                )}
              </RowFixed>
              {onCurrencySelect && <StyledDropDown selected={!!currency} />}
            </Aligner>
          </CurrencySelect>
          {!currency?.isNative && (
            <ContractAddress>
              <ActionDotButton
                type="button"
                aria-label="Token actions"
                aria-expanded={actionsOpen}
                onClick={() => setActionsOpen((open) => !open)}
              >
                <MoreVertical size={16} />
              </ActionDotButton>
              <Popover
                show={actionsOpen}
                placement="bottom-start"
                offsetX={-6}
                offsetY={6}
                hideArrow
                backdropDismiss
                onDismiss={() => setActionsOpen(false)}
                content={
                  <TokenActionsMenu>
                    <TokenActionsMenuItem onClick={handleCopyAddress}>
                      <CopyIcon size={14} />
                      <span>{copied ? <Trans>Address Copied</Trans> : <Trans>Copy Address</Trans>}</span>
                    </TokenActionsMenuItem>
                    <TokenActionsMenuItem onClick={handleAddToWallet}>
                      <PlusCircle size={14} />
                      <Trans>Add to Wallet</Trans>
                    </TokenActionsMenuItem>
                    {(explorerUrl || midgardUrl) && <TokenActionsDivider />}
                    {explorerUrl && (
                      <TokenActionsMenuItem onClick={handleViewOnExplorer}>
                        <ExternalLinkIcon size={14} />
                        <Trans>View on Explorer</Trans>
                      </TokenActionsMenuItem>
                    )}
                    {midgardUrl && (
                      <TokenActionsMenuItem onClick={handleViewOnMidgard}>
                        <Shield size={14} />
                        <Trans>Inspect on Midgard</Trans>
                      </TokenActionsMenuItem>
                    )}
                    {currencyIdValue && (explorerUrl || midgardUrl) && <TokenActionsDivider />}
                    {currencyIdValue && (
                      <TokenActionsMenuItem onClick={handleToggleFavorite}>
                        <FavoriteIcon size={14} $active={isFavorite} />
                        <span>{isFavorite ? <Trans>Remove Favorite</Trans> : <Trans>Add Favorite</Trans>}</span>
                      </TokenActionsMenuItem>
                    )}
                  </TokenActionsMenu>
                }
              />
            </ContractAddress>
          )}
        </InputRow>
        {Boolean(!hideInput && !hideBalance) && (
          <FiatRow>
            <RowBetween>
              <LoadingOpacityContainer $loading={loading}>
                {fiatValue && <FiatValue fiatValue={fiatValue} priceImpact={priceImpact} />}
              </LoadingOpacityContainer>
              {account ? (
                <RowFixed style={{ height: '17px' }}>
                  <ThemedText.DeprecatedBody
                    data-testid="balance-text"
                    color={theme.textSecondary}
                    fontWeight={500}
                    fontSize={13}
                    style={{ display: 'inline' }}
                  >
                    {!hideBalance && currency && selectedCurrencyBalance ? (
                      renderBalance ? (
                        renderBalance(selectedCurrencyBalance)
                      ) : (
                        <Trans>Balance: {formatCurrencyAmount(selectedCurrencyBalance, 8)}</Trans>
                      )
                    ) : null}
                  </ThemedText.DeprecatedBody>
                  {/*showMaxButton && selectedCurrencyBalance ? (
                    <StyledBalanceMax onClick={onMax}>
                      <Trans>Max</Trans>
                    </StyledBalanceMax>
                  ) : null*/}
                  {showMaxButton && selectedCurrencyBalance ? (
                    <div
                      className="dropdown-container"
                      style={{ position: 'relative', display: 'inline-block' }}
                      onMouseEnter={() => !isMobile && setDropdownVisible(true)}
                      onMouseLeave={() => !isMobile && setDropdownVisible(false)}
                    >
                      <StyledBalanceMax onClick={handleMaxClick}>
                        <Trans>Max</Trans>
                      </StyledBalanceMax>
                      {dropdownVisible && (
                        <HorizontalDropdown>
                          {[25, 50, 75].map((perc) => (
                            <DropdownItem key={perc} onClick={() => handlePercentageClick(perc)}>
                              {perc}%
                            </DropdownItem>
                          ))}
                          {isMobile && <DropdownItem onClick={onMax}>Max</DropdownItem>}
                        </HorizontalDropdown>
                      )}
                    </div>
                  ) : null}
                </RowFixed>
              ) : (
                <span />
              )}
            </RowBetween>
          </FiatRow>
        )}
      </Container>
      {onCurrencySelect && (
        <CurrencySearchModal
          isOpen={modalOpen}
          onDismiss={handleDismissSearch}
          onCurrencySelect={onCurrencySelect}
          selectedCurrency={currency}
          otherSelectedCurrency={otherCurrency}
          showCommonBases={showCommonBases}
          showCurrencyAmount={showCurrencyAmount}
          disableNonToken={disableNonToken}
        />
      )}
    </InputPanel>
  )
}
